import React, { useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';

import { UploadMediaFilesDocument } from '@shared/api/graphql/generated';
import { uploadFile, removeUpload, selectUploadItems, UploadStatus } from 'application/store';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';
import { toast } from '@shared/ui';
import { IVideoMediafile } from '../types';

import { TEXTS } from '../texts';
import { getPlayDuration, replaceTextByErrorMessage } from './utils';

interface Props {
  companyId: string;
  refetch: () => void;
  videoMediafiles?: IVideoMediafile[];
  loading: boolean;
  hasPrevMediafiles?: boolean;
}

const acceptFiles = { 'video/mpeg': ['.mp4'], 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] };

export const useSelectVideoMediafilesController = ({
  companyId,
  refetch,
  videoMediafiles,
  loading,
  hasPrevMediafiles,
}: Props) => {
  const [selectedItems, setSelectedItems] = React.useState<IVideoMediafile[]>([]);

  const dispatch = useAppDispatch();
  const uploadItemsById = useAppSelector(selectUploadItems);
  const uploadTag = `video-mediafiles:${companyId}`;

  const nextButtonTitle = hasPrevMediafiles ? TEXTS.NEXT : TEXTS.CREATE_VIDEO_STREAM_MODAL_SELECT_BUTTON;

  const onSelectedChange = (value: boolean, item: IVideoMediafile) => {
    if (value) {
      setSelectedItems([...selectedItems, { ...item, playDuration: getPlayDuration(item) }]);
    } else {
      setSelectedItems(selectedItems.filter((current) => current.id !== item.id));
    }
  };

  const onDrop = (files: File[]) => {
    files.forEach((file) => {
      dispatch(
        uploadFile({
          id: crypto.randomUUID(),
          tag: uploadTag,
          file,
          document: UploadMediaFilesDocument,
          variables: { input: { file, companyId } },
          filePath: 'input.file',
        })
      )
        .unwrap()
        .catch((message: string) => {
          toast.error(replaceTextByErrorMessage(message));
        });
    });
  };

  const { open: onClickUpload, getInputProps } = useDropzone({
    onDrop,
    accept: acceptFiles,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const tagItems = useMemo(
    () => Object.values(uploadItemsById).filter((item) => item.tag === uploadTag),
    [uploadItemsById, uploadTag]
  );

  const uploadingItems = useMemo(() => tagItems.filter((item) => item.status === UploadStatus.UPLOADING), [tagItems]);

  const uploadLoading = uploadingItems.length > 0;

  useEffect(() => {
    tagItems
      .filter((item) => item.status !== UploadStatus.UPLOADING)
      .forEach((item) => dispatch(removeUpload(item.id)));
  }, [tagItems]);

  useEffect(() => {
    if (!loading && !uploadLoading) {
      refetch();
    }
  }, [uploadLoading]);

  const items = React.useMemo(() => {
    const uploadingVideoMediafiles = uploadingItems.map((item) => ({
      title: item.fileName,
      uploading: true,
    }));

    return [...uploadingVideoMediafiles, ...(videoMediafiles || [])];
  }, [uploadingItems, videoMediafiles]);

  return {
    onSelectedChange,
    selectedItems,
    onClickUpload,
    getInputProps,
    uploadLoading,
    items,
    nextButtonTitle,
  };
};
